import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';
import { LightbulbIcon } from 'lucide-react';

import {
  trackerAutomationSuggestionsQueryOptions,
  useDismissTrackerAutomationSuggestionMutation,
  type TrackerAutomationSuggestion,
} from '@/entities/tracker-automation';
import { notificationService } from '@/lib/services/notification.service';

import { automationErrorKey } from '../../lib/automation-errors';

/**
 * Rules the board's own repeated edits suggest. Reviewing one opens it in the
 * builder and leaves it on offer, since backing out of the builder must not
 * lose it; the builder marks it taken once its rule is created. Dismissing one
 * keeps it from coming back. Shows nothing while there is nothing to suggest.
 */
export function AutomationSuggestions({
  trackerId,
  summarize,
  onReview,
}: {
  trackerId: string;
  summarize: (suggestion: TrackerAutomationSuggestion) => string;
  onReview: (suggestion: TrackerAutomationSuggestion) => void;
}) {
  const { t } = useTranslation('trackers');
  const { data } = useQuery(trackerAutomationSuggestionsQueryOptions(trackerId));
  const dismiss = useDismissTrackerAutomationSuggestionMutation({ trackerId });
  const suggestions = data?.data ?? [];
  if (suggestions.length === 0) return null;

  const failed = (error: unknown) =>
    notificationService.showError(
      t(automationErrorKey(error, 'trackersBoard.automationAi.suggestions.failed')),
    );

  return (
    <section
      aria-labelledby="automation-suggestions-title"
      className="flex flex-col gap-2 rounded-lg border border-dashed border-border p-3"
    >
      <h3
        id="automation-suggestions-title"
        className="flex items-center gap-1.5 text-sm font-medium text-foreground"
      >
        <LightbulbIcon className="size-4" aria-hidden />
        {t('trackersBoard.automationAi.suggestions.title')}
      </h3>
      <p className="text-xs text-muted-foreground">
        {t('trackersBoard.automationAi.suggestions.intro')}
      </p>
      <ul className="flex flex-col gap-2">
        {suggestions.map((suggestion) => {
          const summary = summarize(suggestion);
          return (
            <li key={suggestion.id} className="flex items-center gap-3">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm text-foreground">{summary}</span>
                <span className="text-xs text-muted-foreground">
                  {t('trackersBoard.automationAi.suggestions.seenOn', {
                    count: suggestion.support,
                  })}
                </span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={t('trackersBoard.automationAi.suggestions.reviewNamed', { summary })}
                onClick={() => onReview(suggestion)}
              >
                {t('trackersBoard.automationAi.suggestions.review')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={t('trackersBoard.automationAi.suggestions.dismissNamed', { summary })}
                disabled={dismiss.isPending}
                onClick={() => dismiss.mutate({ suggestionId: suggestion.id }, { onError: failed })}
              >
                {t('trackersBoard.automationAi.suggestions.dismiss')}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
